import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  EXT_PREFIX,
  OTHER_INPUT_SUBCONTROL_EXT,
  SUBCONTROL_DEPENDENCY_ACTION,
  UI_CONFIG_EXT,
} from '../constants/fhir.constants';
import type { FHIRExtension, FHIRItem } from '../models/fhir.model';
import { FhirQuestionnaireApiService } from './fhir-questionnaire-api.service';

/** Extension URL stamped on a Questionnaire's root marking which "Other" input format it was generated with. */
export const OTHER_INPUT_FORMAT_EXT = `${EXT_PREFIX}:other-input-format`;

/** Bump this if the "Other" input subcontrol format changes again in the future. */
export const CURRENT_OTHER_INPUT_FORMAT_VERSION = 1;

/** Minimal shape needed to read/write the root format-version marker, without requiring a full FHIRQuestionnaire. */
export interface VersionableQuestionnaire {
  id?: string;
  extension?: FHIRExtension[];
}

/**
 * Questionnaire Format Version Service
 *
 * Centralizes detection and upgrade of the "Other" text-input subcontrol format. If a questionnaire lacks the format
 * version extension (legacy/unversioned), it automatically synthesizes missing "Other" subcontrols, stamps the
 * extension, and persists the updated Questionnaire to FHIR DB via a PUT request.
 */
@Injectable({ providedIn: 'root' })
export class QuestionnaireFormatVersionService {
  private readonly fhirQuestionnaireApi = inject(FhirQuestionnaireApiService, { optional: true });
  /** Stamp the current format version onto a Questionnaire's root extensions (idempotent). */
  stampOtherInputFormat(questionnaire: VersionableQuestionnaire): void {
    if (!questionnaire.extension) {
      questionnaire.extension = [];
    }
    const existing = questionnaire.extension.find((ext) => ext.url === OTHER_INPUT_FORMAT_EXT);
    if (existing) {
      existing.valueInteger = CURRENT_OTHER_INPUT_FORMAT_VERSION;
    } else {
      questionnaire.extension.push({ url: OTHER_INPUT_FORMAT_EXT, valueInteger: CURRENT_OTHER_INPUT_FORMAT_VERSION });
    }
  }

  /** Read the stamped format version, or 0 when the Questionnaire predates this feature (legacy/unversioned). */
  getOtherInputFormatVersion(questionnaire: VersionableQuestionnaire | undefined): number {
    const ext = questionnaire?.extension?.find((e) => e.url === OTHER_INPUT_FORMAT_EXT);
    return typeof ext?.valueInteger === 'number' ? ext.valueInteger : 0;
  }

  /** O(1) check — true when the root marker is missing/stale. Never walks the item tree. */
  needsOtherInputMigration(questionnaire: VersionableQuestionnaire | undefined): boolean {
    return this.getOtherInputFormatVersion(questionnaire) < CURRENT_OTHER_INPUT_FORMAT_VERSION;
  }

  /** Batch helper for admin/reporting tools: returns ids of Questionnaires still needing migration. */
  findQuestionnairesNeedingMigration(questionnaires: VersionableQuestionnaire[]): string[] {
    return questionnaires.filter((q) => this.needsOtherInputMigration(q)).map((q) => q.id ?? '');
  }

  /**
   * Upgrades a legacy questionnaire to the current "Other" subcontrol format. Synthesizes missing Other text-inputs,
   * stamps the root format version, and saves the updated Questionnaire to FHIR DB via a PUT request (unless persist is
   * explicitly false).
   */
  upgradeQuestionnaire<T extends VersionableQuestionnaire & { item?: FHIRItem[] }>(
    questionnaire: T | undefined,
    options?: { persist?: boolean }
  ): Observable<T | undefined> {
    if (!questionnaire || !this.needsOtherInputMigration(questionnaire)) {
      return of(questionnaire);
    }

    this.synthesizeMissingOtherInputs(questionnaire.item);
    this.stampOtherInputFormat(questionnaire);

    const shouldPersist = options?.persist !== false;
    if (shouldPersist && questionnaire.id && this.fhirQuestionnaireApi) {
      return this.fhirQuestionnaireApi.putQuestionnaire(questionnaire as any).pipe(
        map(() => questionnaire),
        catchError((err) => {
          console.warn(
            '[QuestionnaireFormatVersionService] Failed to persist Questionnaire format upgrade to FHIR DB:',
            err
          );
          return of(questionnaire);
        })
      );
    }

    return of(questionnaire);
  }

  /**
   * Lazily injects the missing "Other" text-input subcontrol sibling item for any legacy radio/checkbox item (hasOther
   * enabled, no existing subcontrol yet) found within `items`. Mutates `items` (and nested group item arrays) in place.
   * Mirrors DesignerFhirConverter.flattenOptionSubControls' output shape so the existing reNestFlatSubControls()
   * re-nesting logic picks it up unchanged.
   */
  synthesizeMissingOtherInputs(items: FHIRItem[] | undefined): void {
    if (!Array.isArray(items) || items.length === 0) {
      return;
    }

    const originalLength = items.length;
    for (let i = 0; i < originalLength; i++) {
      const item = items[i];
      if (!item) continue;

      if (item.type === 'choice' || item.type === 'open-choice') {
        this.trySynthesizeForChoiceParent(item, items);
      }

      // Recurse into nested groups (sections/subsections carry their own sibling item[] list).
      if (item.type === 'group' && Array.isArray(item.item)) {
        this.synthesizeMissingOtherInputs(item.item);
      }
    }
  }

  private trySynthesizeForChoiceParent(parent: FHIRItem, siblingList: FHIRItem[]): void {
    const uiConfig = this.parseUiConfig(parent.extension);
    const hasOther = uiConfig?.['hasOther'] === true || uiConfig?.['showOtherOption'] === true;
    if (!hasOther) {
      return;
    }

    const alreadyHasOtherInput = siblingList.some(
      (sibling) =>
        sibling?.extension?.some((ext) => ext.url === OTHER_INPUT_SUBCONTROL_EXT && ext.valueBoolean === true) &&
        this.dependencyTargetsParent(sibling, parent.linkId)
    );
    if (alreadyHasOtherInput) {
      return;
    }

    const optionValue = this.resolveOtherOptionValue(uiConfig, parent);
    siblingList.push(this.buildOtherInputItem(parent.linkId, optionValue));
  }

  private parseUiConfig(extensions: FHIRExtension[] | undefined): Record<string, unknown> {
    const ext = extensions?.find((e) => e.url === UI_CONFIG_EXT);
    if (!ext?.valueString) {
      return {};
    }
    try {
      return JSON.parse(ext.valueString) as Record<string, unknown>;
    } catch {
      return {};
    }
  }

  private resolveOtherOptionValue(uiConfig: Record<string, unknown>, parent: FHIRItem): string {
    const options = Array.isArray(uiConfig['options']) ? (uiConfig['options'] as any[]) : [];
    const matchedUiOption = options.find(
      (o) =>
        o &&
        (String(o.id).toLowerCase() === 'other' ||
          String(o.value).toLowerCase() === 'other' ||
          String(o.label).toLowerCase() === 'other')
    );
    if (matchedUiOption?.value !== undefined && matchedUiOption?.value !== null) {
      return String(matchedUiOption.value);
    }

    const matchedAnswerOption = parent.answerOption?.find(
      (o) => o.valueCoding?.code && String(o.valueCoding.code).toLowerCase() === 'other'
    );
    return matchedAnswerOption?.valueCoding?.code ?? 'other';
  }

  private dependencyTargetsParent(item: FHIRItem, parentLinkId: string): boolean {
    const depExt = item.extension?.find((ext) => ext.url === `${EXT_PREFIX}:dependencies`);
    if (!depExt?.valueString) {
      return false;
    }
    try {
      const parsed = JSON.parse(depExt.valueString);
      const rules: any[] = Array.isArray(parsed?.conditionalLogic) ? parsed.conditionalLogic : [];
      const subRule = rules.find((r) => String(r.Action).toLowerCase() === SUBCONTROL_DEPENDENCY_ACTION);
      return subRule?.Rules?.[0]?.field === parentLinkId;
    } catch {
      return false;
    }
  }

  /** Builds the synthesized "Other" text-input item, matching DesignerFhirConverter's flattenOptionSubControls shape. */
  private buildOtherInputItem(parentLinkId: string, optionValue: string): FHIRItem {
    const isWildcard = optionValue === '*';
    const dependencyRule = {
      Action: SUBCONTROL_DEPENDENCY_ACTION,
      Rules: [
        {
          operator: null,
          field: parentLinkId,
          condition: isWildcard ? 'NotEqualTo' : 'Contains',
          value: isWildcard ? '' : optionValue,
        },
      ],
    };

    return {
      linkId: `${parentLinkId}.other`,
      text: '',
      type: 'string',
      extension: [
        { url: OTHER_INPUT_SUBCONTROL_EXT, valueBoolean: true },
        {
          url: `${EXT_PREFIX}:dependencies`,
          valueString: JSON.stringify({ conditionalLogic: [dependencyRule], dependencyCount: 1 }),
        },
      ],
    };
  }
}
